// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * Pure hierarchy helpers for OpenClaw agent authorization.
 *
 * Inputs are plain teammember-like rows. Local `.flowstate/agents/*.json`
 * files are bootstrap/import artifacts only and are not runtime hierarchy
 * authority. The helpers intentionally avoid RxDB document APIs so MCP, CLI,
 * workers, and tests can all call the same code with already-loaded records.
 */

export interface AgentHierarchyRecord {
  id?: string
  slug?: string | null
  name?: string | null
  userName?: string | null
  role?: string | null
  directReports?: unknown
  metadata?: Record<string, unknown>
  extended?: {
    agent?: {
      name?: string | null
      metadata?: Record<string, unknown>
      [key: string]: unknown
    }
    [key: string]: unknown
  }
  [key: string]: unknown
}

export interface AgentHierarchyCollections {
  teammembers?: AgentHierarchyRecord[] | { records?: AgentHierarchyRecord[] }
  records?: AgentHierarchyRecord[]
}

export type AgentHierarchyInput = AgentHierarchyRecord[] | AgentHierarchyCollections

interface HierarchyNode {
  slug: string
  aliases: Set<string>
  directReports: string[]
}

interface HierarchyIndex {
  nodes: Map<string, HierarchyNode>
  aliasToSlug: Map<string, string>
}

interface AliasBundle {
  canonicalSlug: string
  stableAliases: string[]
  fallbackAliases: string[]
}

interface AliasSource {
  slug: string
  kind: 'stable' | 'fallback'
  stableBacked: boolean
}

export function getDirectReports(input: AgentHierarchyInput, managerSlug: string): string[] {
  const index = buildHierarchyIndex(input)
  const manager = requireNode(index, managerSlug, 'manager')
  return [...manager.directReports]
}

export function getDescendants(input: AgentHierarchyInput, managerSlug: string): string[] {
  const index = buildHierarchyIndex(input)
  const manager = requireNode(index, managerSlug, 'manager')
  const descendants: string[] = []
  const visited = new Set<string>()

  const walk = (node: HierarchyNode): void => {
    for (const childSlug of node.directReports) {
      if (visited.has(childSlug)) continue
      visited.add(childSlug)
      descendants.push(childSlug)
      const child = index.nodes.get(childSlug)
      if (child) walk(child)
    }
  }

  walk(manager)
  return descendants
}

export function assertCanControl(
  input: AgentHierarchyInput,
  managerSlug: string,
  childSlug: string
): true {
  const index = buildHierarchyIndex(input)
  const manager = requireNode(index, managerSlug, 'manager')
  const target = requireNode(index, childSlug, 'target')

  if (manager.slug === target.slug) {
    throw new Error(`Agent hierarchy authorization rejected self-control for "${manager.slug}"`)
  }

  if (!manager.directReports.includes(target.slug)) {
    throw new Error(
      `Agent hierarchy authorization rejected: ${manager.slug} cannot control ${target.slug}; ` +
        'control is limited to direct reports'
    )
  }

  return true
}

export function assertCanReport(
  input: AgentHierarchyInput,
  managerSlug: string,
  targetSlug: string
): true {
  const index = buildHierarchyIndex(input)
  const manager = requireNode(index, managerSlug, 'manager')
  const target = requireNode(index, targetSlug, 'target')

  if (manager.slug === target.slug) {
    throw new Error(`Agent hierarchy authorization rejected self-reporting for "${manager.slug}"`)
  }

  const descendants = getDescendantsFromIndex(index, manager.slug)
  if (!descendants.includes(target.slug)) {
    throw new Error(
      `Agent hierarchy authorization rejected: ${manager.slug} cannot report on ${target.slug}; ` +
        'reporting is limited to descendants'
    )
  }

  return true
}

function getDescendantsFromIndex(index: HierarchyIndex, managerSlug: string): string[] {
  const manager = index.nodes.get(managerSlug)
  if (!manager) return []
  const descendants: string[] = []
  const visited = new Set<string>()

  const walk = (node: HierarchyNode): void => {
    for (const childSlug of node.directReports) {
      if (visited.has(childSlug)) continue
      visited.add(childSlug)
      descendants.push(childSlug)
      const child = index.nodes.get(childSlug)
      if (child) walk(child)
    }
  }

  walk(manager)
  return descendants
}

function buildHierarchyIndex(input: AgentHierarchyInput): HierarchyIndex {
  const records = collectRecords(input)
  const nodes = new Map<string, HierarchyNode>()
  const aliasToSlug = new Map<string, string>()
  const aliasSources = new Map<string, AliasSource>()
  const ambiguousFallbackAliases = new Set<string>()

  for (const record of records) {
    const aliases = resolveAliasBundle(record)
    const slug = aliases.canonicalSlug
    if (!slug) continue

    if (nodes.has(slug)) {
      throw new Error(`Agent hierarchy authorization rejected: duplicate slug "${slug}"`)
    }

    const node: HierarchyNode = {
      slug,
      aliases: new Set<string>(),
      directReports: [],
    }

    for (const alias of aliases.stableAliases) {
      const existing = aliasSources.get(alias)
      if (existing) {
        if (existing.kind === 'fallback' && existing.stableBacked) {
          aliasToSlug.set(alias, slug)
          aliasSources.set(alias, { slug, kind: 'stable', stableBacked: true })
          node.aliases.add(alias)
          continue
        }
        throw new Error(
          `Agent hierarchy authorization rejected: alias collision for "${alias}" ` +
            `between "${existing.slug}" and "${slug}"`
        )
      }
      node.aliases.add(alias)
      aliasToSlug.set(alias, slug)
      aliasSources.set(alias, { slug, kind: 'stable', stableBacked: true })
    }

    for (const alias of aliases.fallbackAliases) {
      if (ambiguousFallbackAliases.has(alias)) continue
      const existing = aliasSources.get(alias)
      if (existing) {
        if (existing.slug === slug) continue
        if (existing.kind === 'stable' && aliases.stableAliases.length > 0) continue
        if (
          existing.kind === 'fallback' &&
          existing.stableBacked &&
          aliases.stableAliases.length > 0
        ) {
          aliasSources.delete(alias)
          aliasToSlug.delete(alias)
          ambiguousFallbackAliases.add(alias)
          continue
        }
        throw new Error(
          `Agent hierarchy authorization rejected: alias collision for "${alias}" ` +
            `between "${existing.slug}" and "${slug}"`
        )
      }
      node.aliases.add(alias)
      aliasToSlug.set(alias, slug)
      aliasSources.set(alias, {
        slug,
        kind: 'fallback',
        stableBacked: aliases.stableAliases.length > 0,
      })
    }

    const reports = readDirectReports(record).map(normalizeSlug).filter(isNonEmptyString)
    node.directReports = mergeUnique(node.directReports, reports)
    nodes.set(slug, node)
  }

  for (const node of nodes.values()) {
    node.directReports = node.directReports.map(slug => aliasToSlug.get(slug) ?? slug)
  }

  validateAcyclic(nodes)

  return { nodes, aliasToSlug }
}

function collectRecords(input: AgentHierarchyInput): AgentHierarchyRecord[] {
  if (Array.isArray(input)) return input

  return [...recordsFromSource(input.records), ...recordsFromSource(input.teammembers)]
}

function recordsFromSource(
  source: AgentHierarchyRecord[] | { records?: AgentHierarchyRecord[] } | undefined
): AgentHierarchyRecord[] {
  if (!source) return []
  if (Array.isArray(source)) return source
  return source.records ?? []
}

function validateAcyclic(nodes: Map<string, HierarchyNode>): void {
  const visited = new Set<string>()
  const visiting = new Set<string>()

  const visit = (slug: string, path: string[]): void => {
    if (visited.has(slug)) return
    if (visiting.has(slug)) {
      const cycleStart = path.indexOf(slug)
      const cyclePath = [...path.slice(cycleStart), slug].join(' -> ')
      throw new Error(`Agent hierarchy authorization rejected: cycle detected (${cyclePath})`)
    }

    const node = nodes.get(slug)
    if (!node) return

    visiting.add(slug)
    for (const childSlug of node.directReports) {
      visit(childSlug, [...path, childSlug])
    }
    visiting.delete(slug)
    visited.add(slug)
  }

  for (const slug of nodes.keys()) {
    visit(slug, [slug])
  }
}

function requireNode(
  index: HierarchyIndex,
  slug: string,
  kind: 'manager' | 'target'
): HierarchyNode {
  const normalized = normalizeSlug(slug)
  const canonicalSlug = index.aliasToSlug.get(normalized) ?? normalized
  const node = index.nodes.get(canonicalSlug)
  if (!node) {
    throw new Error(`Agent hierarchy authorization rejected: unknown ${kind} slug "${normalized}"`)
  }
  return node
}

function resolveAliasBundle(record: AgentHierarchyRecord): AliasBundle {
  const metadata = record.metadata ?? {}
  const extendedAgent = record.extended?.agent
  const extendedAgentMetadata = extendedAgent?.metadata ?? {}
  const stableAliases = [
    normalizeMaybe(record.slug),
    normalizeMaybe(metadata['slug']),
    normalizeMaybe(extendedAgent?.name),
  ].filter(isNonEmptyString)
  const fallbackAliases = mergeUnique(
    [],
    [
      normalizeMaybe(record.name),
      normalizeMaybe(record.userName),
      normalizeMaybe(metadata['name']),
      normalizeMaybe(metadata['orgRole']),
      normalizeMaybe(extendedAgentMetadata['orgRole']),
    ].filter(isNonEmptyString)
  )
  const canonicalSlug = stableAliases[0] ?? fallbackAliases[0] ?? ''

  return {
    canonicalSlug,
    stableAliases: mergeUnique([], stableAliases),
    fallbackAliases,
  }
}

function readDirectReports(record: AgentHierarchyRecord): string[] {
  const metadata = record.metadata ?? {}
  const extendedAgentMetadata = record.extended?.agent?.metadata ?? {}

  return [
    ...readSlugArray(record.directReports),
    ...readSlugArray(metadata['directReports']),
    ...readSlugArray(extendedAgentMetadata['directReports']),
  ]
}

function readSlugArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []

  return value
    .map(item => {
      if (typeof item === 'string') return directReportSlugFromString(item)
      if (!isRecord(item)) return null
      return (
        stringValue(item['slug']) ??
        stringValue(item['name']) ??
        stringValue(item['userName']) ??
        stringValue(item['orgRole'])
      )
    })
    .filter(isNonEmptyString)
}

function directReportSlugFromString(value: string): string | null {
  const raw = stringValue(value)
  if (!raw) return null
  return raw.split(':', 1)[0] ?? null
}

function normalizeMaybe(value: unknown): string | null {
  const raw = stringValue(value)
  return raw ? normalizeSlug(raw) : null
}

function normalizeSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\b(?:of|the|and)\b/g, '')
    .replace(/[()]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isNonEmptyString(value: string | null): value is string {
  return typeof value === 'string' && value.length > 0
}

function mergeUnique(existing: string[], next: string[]): string[] {
  const seen = new Set(existing)
  const merged = [...existing]
  for (const value of next) {
    if (seen.has(value)) continue
    seen.add(value)
    merged.push(value)
  }
  return merged
}
